
import mapboxgl from 'mapbox-gl';
import { Terrace } from '@/types/terrace';
import { NavigateFunction } from 'react-router-dom';
import { createMapPopup } from './MapPopup';

interface CreateMapMarkerProps {
  terrace: Terrace;
  map: mapboxgl.Map;
  popupRef: React.MutableRefObject<mapboxgl.Popup | null>;
  navigate: NavigateFunction;
  interactive?: boolean;
}

export const createMapMarker = ({ 
  terrace, 
  map, 
  popupRef, 
  navigate,
  interactive = true
}: CreateMapMarkerProps) => {
  
  const createMarker = () => {
    // Create marker element
    const markerElement = document.createElement('div');
    markerElement.className = `
      w-8 h-8 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold
      ${terrace.sunStatus === 'sunny' ? 'bg-peach-orange' : 
        terrace.sunStatus === 'soon' ? 'bg-sunset-yellow' : 
        'bg-sunset-medium'}
      ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
    `;
    
    // Use appropriate icon based on sun status
    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'sunny':
          return '☀️';
        case 'soon':
          return '⏳';
        default:
          return '☁️';
      }
    };
    
    markerElement.textContent = getStatusIcon(terrace.sunStatus);

    // Create marker
    const marker = new mapboxgl.Marker(markerElement)
      .setLngLat([terrace.lng, terrace.lat])
      .addTo(map);

    // Only add click handlers if interactive
    if (interactive) {
      // Add click handler to marker element
      markerElement.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Close existing popup
        if (popupRef.current) {
          popupRef.current.remove();
        }

        // Create and show new popup
        const { createPopupContent } = createMapPopup(terrace, navigate);
        const popupContent = createPopupContent();
        
        const popup = new mapboxgl.Popup({ offset: 25 })
          .setDOMContent(popupContent)
          .setLngLat([terrace.lng, terrace.lat])
          .addTo(map);
          
        popupRef.current = popup;
      });
    }

    return marker;
  };

  return { createMarker };
};
